import React, { useState, useEffect, useRef } from 'react';
import {
  SparklesIcon,
  SendIcon,
  LogOutIcon,
  SettingsIcon,
  RefreshCwIcon,
  EditIcon,
  CheckIcon,
  XIcon,
  AlertTriangleIcon,
  ZapIcon,
  SwordsIcon,
  GitBranchIcon
} from 'lucide-react';
import ThreadSidebar from './ThreadSidebar';
import ThreadMessages from './ThreadMessages';
import SoloChat from './SoloChat';
import SummarizeModal from './SummarizeModal';
import ModelBox from './ModelBox';
import TournamentResults from './TournamentResults';
import ParallelUniversesResults from './ParallelUniversesResults';
import MultiverseProgress from './MultiverseProgress';
import TournamentProgress from './TournamentProgress';
import FileUpload from './FileUpload';
import { useSettings } from '../SettingsContext';
import SettingsModal from './SettingsModal';
import CaveatsModal from './CaveatsModal';
import personalities from '../utils/personalities';
import { resolveFriendlyName } from '../shared/modelMap';
import Footer from './Footer';
import { updateGlobalStats } from '../utils/globalStats';

const Forge = ({ token, onLogout }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [currentResults, setCurrentResults] = useState(null);
  const [tournamentResults, setTournamentResults] = useState(null);
  const [parallelUniversesResults, setParallelUniversesResults] = useState(null);
  const [error, setError] = useState('');
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showCaveats, setShowCaveats] = useState(false);
  const { settings, setTournamentMode, setSoloAgent } = useSettings();
  const viewMode = settings.viewMode || 'tournament'; // 'tournament' or 'solo'
  const isSoloMode = viewMode === 'solo';
  const [usage, setUsage] = useState({ used: 0, maxPrompts: 0, remaining: 0 });
  const [customPersonas, setCustomPersonas] = useState({ gpt: {}, claude: {}, gemini: {} });
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showSummarizeModal, setShowSummarizeModal] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  // Tournament mode: 'manual' or 'auto'
  const tournamentMode = settings.tournamentMode || 'manual';

  // Merge built-in and custom personalities
  const mergedPersonalities = React.useMemo(() => ({
    gpt: { ...personalities.gpt, ...customPersonas.gpt },
    claude: { ...personalities.claude, ...customPersonas.claude },
    gemini: { ...personalities.gemini, ...customPersonas.gemini }
  }), [customPersonas]);

  // Load usage on mount
	useEffect(() => {
	  let retryCount = 0;
	  const loadUsage = async () => {
	    try {
	      const response = await fetch('/api/usage', {
	        headers: {
	          'Authorization': `Bearer ${token}`,
	          'Content-Type': 'application/json'
	        }
	      });

	      if (response.ok) {
	        const data = await response.json();
	        if (data.usage && data.usage.maxPrompts >= 0) {
	          setUsage({
	            used: data.usage.used,
	            maxPrompts: data.usage.maxPrompts,
	            remaining: data.usage.remaining
	          });
	          return; // success — stop retrying
	        }
	      }
	      // Retry logic if usage not ready yet
	      if (retryCount < 5) { // retry up to 5 times
	        retryCount++;
	        setTimeout(loadUsage, 1500); // wait 1.5s before retry
	      } else {
	        console.warn('[FORGE] Usage not available after retries');
	      }
	    } catch (error) {
	      console.error('[FORGE] Failed to load usage:', error);
	      if (retryCount < 5) {
	        retryCount++;
	        setTimeout(loadUsage, 2000);
	      }
	    }
	  };

	  loadUsage();
	}, [token]);

  // Load custom personas on mount
  useEffect(() => {
    const loadCustomPersonas = async () => {
      try {
        const response = await fetch('/api/custom-personas', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.personas && Array.isArray(data.personas)) {
            const personasByModel = { gpt: {}, claude: {}, gemini: {} };

            data.personas.forEach(persona => {
              const key = `custom_${persona._id}`;
              personasByModel.gpt[key] = { name: persona.name, description: persona.description };
              personasByModel.claude[key] = { name: persona.name, description: persona.description };
              personasByModel.gemini[key] = { name: persona.name, description: persona.description };
            });

            setCustomPersonas(personasByModel);
          }
        }
      } catch (error) {
        console.error('[FORGE] Failed to load custom personas:', error);
      }
    };

    loadCustomPersonas();
  }, [token]);

  // Load threads on mount
  useEffect(() => {
    const loadInitialThreads = async () => {
      try {
        setThreadsLoading(true);
        const response = await fetch('/api/threads', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load threads');
        }

        const data = await response.json();
        setThreads(data.threads);

        // Auto-select first thread if none selected
        if (data.threads.length > 0 && !activeThreadId) {
          setActiveThreadId(data.threads[0].id);
        }
      } catch (error) {
        console.error('Failed to load threads:', error);
        setError('Failed to load threads');
      } finally {
        setThreadsLoading(false);
      }
    };

    loadInitialThreads();
  }, [token, activeThreadId]);

  // Load specific thread when activeThreadId changes
  useEffect(() => {
    const loadSpecificThread = async (threadId) => {
      try {
        const response = await fetch(`/api/threads/${threadId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load thread');
        }

        const data = await response.json();
        setActiveThread(data.thread);
      } catch (error) {
        console.error('Failed to load thread:', error);
        setError('Failed to load thread');
      }
    };

    if (activeThreadId) {
      loadSpecificThread(activeThreadId);
      // Clear current results when switching threads
      setCurrentResults(null);
      setTournamentResults(null);
      setParallelUniversesResults(null);
    } else {
      setActiveThread(null);
      setCurrentResults(null);
      setTournamentResults(null);
      setParallelUniversesResults(null);
    }
  }, [activeThreadId, token]);

  // Handle mode change: clear active thread if it belongs to the other mode
  // Use a ref to track the previous viewMode and detect changes
  const prevViewModeRef = useRef(viewMode);

  useEffect(() => {
    const prevViewMode = prevViewModeRef.current;

    // Only run logic if viewMode actually changed
    if (prevViewMode !== viewMode) {
      prevViewModeRef.current = viewMode;

      // Mode changed - find a thread that matches the new mode
      const matchingThreads = threads.filter(t =>
        viewMode === 'solo' ? t.mode === 'solo' : t.mode !== 'solo'
      );

      if (matchingThreads.length > 0) {
        // Select the first thread of the new mode
        setActiveThreadId(matchingThreads[0].id);
      } else {
        // No threads in current mode, clear selection
        setActiveThreadId(null);
        setActiveThread(null);
        setCurrentResults(null);
        setTournamentResults(null);
        setParallelUniversesResults(null);
      }
    } else if (activeThread) {
      // viewMode didn't change, but check if current thread matches mode
      const threadMode = activeThread.mode || 'tournament';
      const isThreadMatchingMode = (viewMode === 'solo' && threadMode === 'solo') ||
                                    (viewMode !== 'solo' && threadMode !== 'solo');

      if (!isThreadMatchingMode) {
        const matchingThreads = threads.filter(t =>
          viewMode === 'solo' ? t.mode === 'solo' : t.mode !== 'solo'
        );

        if (matchingThreads.length > 0) {
          setActiveThreadId(matchingThreads[0].id);
        } else {
          setActiveThreadId(null);
          setActiveThread(null);
          setCurrentResults(null);
          setTournamentResults(null);
          setParallelUniversesResults(null);
        }
      }
    }
  }, [viewMode, threads, activeThread]);

  const loadThreads = async () => {
    try {
      setThreadsLoading(true);
      // Load all threads (both modes) for sidebar display
      const response = await fetch('/api/threads', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load threads');
      }

      const data = await response.json();
      setThreads(data.threads);

      // Update global statistics whenever threads are loaded (only for tournament threads)
      const tournamentThreads = data.threads.filter(t => t.mode !== 'solo');
      updateGlobalStats(tournamentThreads);

      // Auto-select first thread of current mode if none selected
      if (data.threads.length > 0 && !activeThreadId) {
        const modeThreads = data.threads.filter(t => isSoloMode ? t.mode === 'solo' : t.mode !== 'solo');
        if (modeThreads.length > 0) {
          setActiveThreadId(modeThreads[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load threads:', error);
      setError('Failed to load threads');
    } finally {
      setThreadsLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      // Clear current results
      setCurrentResults(null);
      setTournamentResults(null);
      setParallelUniversesResults(null);
      setError('');

      // Reload threads
      await loadThreads();

      // Reload active thread if one is selected
      if (activeThreadId) {
        await loadThread(activeThreadId);
      }

      // Reload usage
      const usageResponse = await fetch('/api/usage', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (usageResponse.ok) {
        const data = await usageResponse.json();
        if (data.usage && data.usage.maxPrompts >= 0) {
          setUsage({
            used: data.usage.used,
            maxPrompts: data.usage.maxPrompts,
            remaining: data.usage.remaining
          });
        }
      }
    } catch (error) {
      console.error('Failed to refresh:', error);
      setError('Failed to refresh data');
    }
  };

  const loadThread = async (threadId) => {
    try {
      const response = await fetch(`/api/threads/${threadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load thread');
      }

      const data = await response.json();
      setActiveThread(data.thread);
    } catch (error) {
      console.error('Failed to load thread:', error);
      setError('Failed to load thread');
    }
  };

  const handleNewThread = async () => {
    try {
      const response = await fetch('/api/threads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mode: isSoloMode ? 'solo' : 'tournament'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create thread');
      }

      const data = await response.json();

      // Reload threads and select the new one
      await loadThreads();
      setActiveThreadId(data.threadId);
      setCurrentResults(null);
      setTournamentResults(null);
      setParallelUniversesResults(null);
      setError('');
    } catch (error) {
      console.error('Failed to create thread:', error);
      setError('Failed to create new thread');
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this thread?')) {
      return;
    }

    try {
      const response = await fetch(`/api/threads/${threadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete thread');
      }

      // Reload threads
      await loadThreads();
      
      // Clear active thread if it was deleted
      if (activeThreadId === threadId) {
        const remainingThreads = threads.filter(t => t.id !== threadId);
        if (remainingThreads.length > 0) {
          setActiveThreadId(remainingThreads[0].id);
        } else {
          setActiveThreadId(null);
          setActiveThread(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete thread:', error);
      setError('Failed to delete thread');
    }
  };

  const handleStartEditTitle = (threadId, currentTitle) => {
    setEditingThreadId(threadId);
    setEditingTitle(currentTitle);
  };

  const handleSaveTitle = async () => {
    if (!editingTitle.trim()) {
      setError('Thread title cannot be empty');
      return;
    }

    try {
      const response = await fetch(`/api/threads/${editingThreadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ title: editingTitle.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to update thread title');
      }

      // Reload threads and current thread
      await loadThreads();
      if (activeThreadId === editingThreadId) {
        await loadThread(editingThreadId);
      }

      setEditingThreadId(null);
      setEditingTitle('');
      setError('');
    } catch (error) {
      console.error('Failed to update thread title:', error);
      setError('Failed to update thread title');
    }
  };

  const handleCancelEdit = () => {
    setEditingThreadId(null);
    setEditingTitle('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError('');
    setCurrentResults(null);
    setTournamentResults(null);
    setParallelUniversesResults(null);

    try {
      // Get or create thread
      let currentThreadId = activeThreadId;
      if (!currentThreadId) {
        const threadResponse = await fetch('/api/threads', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!threadResponse.ok) {
          throw new Error('Failed to create thread');
        }

        const threadData = await threadResponse.json();
        currentThreadId = threadData.threadId;

        setActiveThreadId(currentThreadId);
        await loadThreads();
      }

      // Prepare media attachments for API (only send necessary data)
      const mediaAttachments = attachedFiles.length > 0
        ? attachedFiles.map(f => ({
            name: f.name,
            type: f.type,
            data: f.data
          }))
        : null;

      // Check tournament mode and call appropriate endpoint
      if (tournamentMode === 'multiverse') {
        // PARALLEL UNIVERSES / MULTIVERSE MODE
        const response = await fetch('/api/parallel-universes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            prompt: prompt.trim(),
            threadId: currentThreadId,
            preferences: settings,
            media: mediaAttachments
          })
        });

        const data = await response.json();

        // Handle errors
        if (!response.ok) {
          if (response.status === 429) {
            setError(`${data.message}`);
            if (data.usage) {
              setUsage(data.usage);
            }
            return;
          }
          throw new Error(data.error || 'Parallel universes failed');
        }

        // Update usage
        if (data.usage) {
          setUsage(data.usage);
        }

        // Set parallel universes results
        setParallelUniversesResults(data);
        setPrompt('');
        setAttachedFiles([]); // Clear files after successful submission

        // Update thread ID if new thread was created
        if (data.threadId && data.threadId !== currentThreadId) {
          setActiveThreadId(data.threadId);
        }

        // Reload thread and threads list
        await loadThread(data.threadId || currentThreadId);
        await loadThreads();

      } else if (tournamentMode === 'auto') {
        // AUTO-TOURNAMENT MODE
        const response = await fetch('/api/auto-tournament', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            prompt: prompt.trim(),
            threadId: currentThreadId,
            preferences: settings,
            media: mediaAttachments
          })
        });

        const data = await response.json();

        // Handle errors
        if (!response.ok) {
          if (response.status === 429) {
            setError(`${data.message}`);
            if (data.usage) {
              setUsage(data.usage);
            }
            return;
          }
          throw new Error(data.error || 'Auto-tournament failed');
        }

        // Update usage
        if (data.usage) {
          setUsage(data.usage);
        }

        // Set tournament results
        setTournamentResults(data);
        setPrompt('');
        setAttachedFiles([]); // Clear files after successful submission

        // Update thread ID if new thread was created
        if (data.threadId && data.threadId !== currentThreadId) {
          setActiveThreadId(data.threadId);
        }

        // Reload thread and threads list
        await loadThread(data.threadId || currentThreadId);
        await loadThreads();

      } else {
        // MANUAL MODE (existing behavior)
        const response = await fetch('/api/forge', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            prompt: prompt.trim(),
            threadId: currentThreadId,
            preferences: settings,
            media: mediaAttachments
          })
        });

        const data = await response.json();

        // Handle prompt limit errors
        if (!response.ok) {
          if (response.status === 429) {
            setError(`${data.message} You have used ${data.usage?.used || 0} of ${data.usage?.maxPrompts || 0} prompts.`);
            if (data.usage) {
              setUsage(data.usage);
            }
            return;
          }
          throw new Error(data.error || 'Request failed');
        }

        // Refresh usage after successful prompt
        if (data.usage) {
          setUsage(data.usage);
        }

        setCurrentResults(data);
        setPrompt('');
        setAttachedFiles([]); // Clear files after successful submission

        // Reload the thread to show updated messages
        await loadThread(currentThreadId);
        await loadThreads();
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Analyze responses from other AIs
  const handleAnalyzeResponse = async (originalPrompt, targetModel, exchangeOrData) => {
    setLoading(true);
    setError('');
    
    try {
      let targetResponse;
      let actualOriginalPrompt = originalPrompt;
      
      if (!actualOriginalPrompt && activeThread && activeThread.messages.length > 0) {
        const firstExchange = activeThread.messages[0];
        if (firstExchange && !firstExchange.prompt.startsWith('[ANALYSIS]')) {
          actualOriginalPrompt = firstExchange.prompt;
        }
      }
      
      if (exchangeOrData && exchangeOrData.responses) {
        const modelKey = targetModel === 'GPT-4o Mini' ? 'gpt' : 
                        targetModel === 'Claude Opus 4' ? 'claude' : 'gemini';
        targetResponse = exchangeOrData.responses[modelKey]?.content;
      } else {
        if (!currentResults || !currentResults.responses) {
          throw new Error('No current results available for analysis');
        }
        
        const modelKey = targetModel === 'GPT-4o Mini' ? 'gpt' : 
                        targetModel === 'Claude Opus 4' ? 'claude' : 'gemini';
        
        const targetResponseObj = currentResults.responses[modelKey];
        if (!targetResponseObj) {
          throw new Error(`No ${targetModel} further analysis is possible`);
        }
        
        targetResponse = targetResponseObj.content;
      }
      
      if (!targetResponse) {
        throw new Error(`No response content found for ${targetModel}`);
      }
      
      if (!actualOriginalPrompt) {
        throw new Error('Could not determine original prompt');
      }
      
      let actualModelId = targetModel;
      
      if (targetModel.includes('GPT')) {
        actualModelId = settings.gpt?.model || 'gpt-4o-mini';
      } else if (targetModel.includes('Claude')) {
        actualModelId = settings.claude?.model || 'claude-opus-4-20250514';  
      } else if (targetModel.includes('Gemini')) {
        actualModelId = settings.gemini?.model || 'gemini-2.5-flash';
      }
      
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          originalPrompt: actualOriginalPrompt,
          targetResponse,
          targetModel: actualModelId,
          threadId: activeThreadId,
          preferences: settings
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle prompt limit errors
        if (response.status === 429) {
          setError(`${errorData.message || 'Prompt limit reached'}`);
          if (errorData.usage) {
            setUsage(errorData.usage);
          }
          return;
        }
        
        throw new Error(errorData.error || 'Analysis failed');
      }

      const data = await response.json();
      
      // UPDATED: Refresh usage after successful analysis
      if (data.usage) {
        setUsage(data.usage);
      }
      
      setCurrentResults(data);

      setActiveThread((prev) => {
        if (!prev) return prev;
        const updatedMessages = prev.messages.map((msg) =>
          msg.id === data.exchangeId
            ? { ...msg, ...data }
            : msg
        );
        return { ...prev, messages: updatedMessages };
      });
      
      await loadThread(activeThreadId);
      await loadThreads();

    } catch (error) {
      setError(`Analysis failed: ${error.message}`);
      console.error('Analysis error details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Solo mode message handler
  const handleSoloSendMessage = async (message, agent, files = []) => {
    setLoading(true);
    setError('');

    try {
      // Get or create solo thread
      let currentThreadId = activeThreadId;
      if (!currentThreadId) {
        const threadResponse = await fetch('/api/threads', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ mode: 'solo' })
        });

        if (!threadResponse.ok) {
          throw new Error('Failed to create thread');
        }

        const threadData = await threadResponse.json();
        currentThreadId = threadData.threadId;
        setActiveThreadId(currentThreadId);
        await loadThreads();
      }

      // Prepare media attachments
      const mediaAttachments = files.length > 0
        ? files.map(f => ({
            name: f.name,
            type: f.type,
            data: f.data
          }))
        : null;

      // Call solo endpoint
      const response = await fetch('/api/solo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: message,
          threadId: currentThreadId,
          agent,
          preferences: settings,
          media: mediaAttachments
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError(data.message || 'Prompt limit reached');
          if (data.usage) {
            setUsage(data.usage);
          }
          return;
        }
        throw new Error(data.error || 'Failed to send message');
      }

      // Update usage
      if (data.usage) {
        setUsage(data.usage);
      }

      // Reload thread to show new message
      await loadThread(currentThreadId);
      await loadThreads();

    } catch (error) {
      setError(`Failed to send message: ${error.message}`);
      console.error('[Solo] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Solo mode summarize handler
  const handleSummarizeConversation = async (agent) => {
    if (!activeThreadId || summarizing) return;

    setSummarizing(true);
    setShowSummarizeModal(false);
    setError('');

    try {
      const response = await fetch('/api/solo/summarize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          threadId: activeThreadId,
          agent,
          preferences: settings
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError(data.message || 'Prompt limit reached');
          if (data.usage) {
            setUsage(data.usage);
          }
          return;
        }
        throw new Error(data.error || 'Failed to summarize conversation');
      }

      // Update usage
      if (data.usage) {
        setUsage(data.usage);
      }

      // Switch to the new thread
      setActiveThreadId(data.newThreadId);
      await loadThreads();
      await loadThread(data.newThreadId);

    } catch (error) {
      setError(`Failed to summarize: ${error.message}`);
      console.error('[Solo Summarize] Error:', error);
    } finally {
      setSummarizing(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header - Full Width with Premium Design */}
      <div className="sticky top-0 z-50 glass border-b border-slate-700/50 shadow-2xl">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1920px]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-4">
            {/* Left: Logo & Branding */}
            <div className="flex items-center flex-wrap gap-2 sm:gap-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src="/NFLogo.png"
                    alt="NeuroForge Logo"
                    className="h-12 w-12 sm:h-14 sm:w-14 object-contain drop-shadow-lg"
                  />
                  <div className="absolute inset-0 h-8 w-8 sm:h-10 sm:w-10 bg-cyan-400/20 rounded-full blur-xl"></div>
                </div>
                <h1 className="text-2xl sm:text-3xl font-black gradient-text tracking-tight">NeuroForge</h1>
              </div>
              <span className="hidden sm:inline-block px-3 py-1.5 glass text-cyan-300 text-xs font-medium rounded-full border border-cyan-500/30">
                AI Battle Arena
              </span>
              {/* Usage indicator */}
              <span className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
                usage.remaining > 2
                  ? 'bg-green-500/20 text-green-300 border-green-500/30'
                  : usage.remaining > 0
                  ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                  : 'bg-red-500/20 text-red-300 border-red-500/30'
              }`}>
                {usage.remaining > 0 || usage.maxPrompts > 0
                  ? `${usage.remaining} prompt${usage.remaining !== 1 ? 's' : ''} left`
                  : 'Loading...'}
              </span>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 glass hover:bg-slate-700/50 text-slate-300 hover:text-white rounded-lg transition-all font-medium text-sm border border-transparent hover:border-cyan-500/30"
                title="Refresh all data"
              >
                <RefreshCwIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={() => setShowCaveats(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 glass hover:bg-yellow-500/20 text-slate-300 hover:text-yellow-300 rounded-lg transition-all font-medium text-sm border border-transparent hover:border-yellow-500/30"
                title="Important caveats & guidelines"
              >
                <AlertTriangleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Caveats</span>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 glass hover:bg-slate-700/50 text-slate-300 hover:text-white rounded-lg transition-all font-medium text-sm border border-transparent hover:border-cyan-500/30"
              >
                <SettingsIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </button>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 glass hover:bg-red-500/20 text-slate-300 hover:text-red-300 rounded-lg transition-all font-medium text-sm border border-transparent hover:border-red-500/30"
              >
                <LogOutIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1920px] py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Thread Sidebar - Wider on large screens */}
          <div className="lg:col-span-3">
            <ThreadSidebar
              threads={threads}
              activeThreadId={activeThreadId}
              onSelectThread={setActiveThreadId}
              onNewThread={handleNewThread}
              onDeleteThread={handleDeleteThread}
              onEditTitle={handleStartEditTitle}
              loading={threadsLoading}
              editingThreadId={editingThreadId}
              editingTitle={editingTitle}
              onTitleChange={setEditingTitle}
              onSaveTitle={handleSaveTitle}
              onCancelEdit={handleCancelEdit}
              viewMode={viewMode}
            />
          </div>

          {/* Main Content - Takes up more space */}
          <div className="lg:col-span-9 space-y-6">
            {/* Conditional rendering based on view mode */}
            {isSoloMode ? (
              /* Solo Mode Chat Interface */
              <div className="glass border border-slate-700/50 rounded-2xl shadow-xl h-[calc(100vh-200px)] overflow-hidden">
                <SoloChat
                  token={token}
                  thread={activeThread}
                  loading={loading}
                  onSendMessage={handleSoloSendMessage}
                  onNewThread={handleNewThread}
                  onSummarize={() => setShowSummarizeModal(true)}
                  summarizing={summarizing}
                  promptsRemaining={usage.remaining}
                />
              </div>
            ) : (
              /* Tournament Mode Interface */
              <>
            {/* Thread Title Editor - Enhanced */}
            {activeThread && (
              <div className="glass border border-slate-700/50 rounded-2xl p-5 shadow-xl">
                {editingThreadId === activeThreadId ? (
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      className="flex-1 bg-slate-900/70 border border-slate-600 rounded-xl px-4 py-3 text-white font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      placeholder="Enter thread title"
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveTitle()}
                    />
                    <button
                      onClick={handleSaveTitle}
                      className="p-3 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-xl transition-all border border-green-500/30"
                      title="Save title"
                    >
                      <CheckIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl transition-all border border-red-500/30"
                      title="Cancel editing"
                    >
                      <XIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white">{activeThread.title}</h2>
                    <button
                      onClick={() => handleStartEditTitle(activeThreadId, activeThread.title)}
                      className="p-2 text-slate-400 hover:text-cyan-400 transition-colors rounded-lg hover:bg-slate-700/50"
                      title="Edit thread title"
                    >
                      <EditIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Prompt Input - Enhanced */}
            <div className="glass border border-slate-700/50 rounded-2xl p-6 sm:p-8 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Tournament Mode Toggle */}
                <div className="flex items-center justify-between">
                  <label className="block text-base font-semibold text-slate-200 flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-cyan-400" />
                    {activeThread ? `Continue conversation in "${activeThread.title}"` : 'Start a new AI Battle'}
                  </label>

                  {/* Mode Toggle - 3-way segmented control */}
                  <div className="flex items-center">
                    <div className="inline-flex rounded-xl bg-slate-800/80 p-1 border border-slate-700/50">
                      {/* Manual Mode */}
                      <button
                        type="button"
                        onClick={() => setTournamentMode('manual')}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          tournamentMode === 'manual'
                            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-lg shadow-cyan-500/20'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                        }`}
                        title="Manual Mode - 1 prompt per action"
                      >
                        <SwordsIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Manual</span>
                      </button>

                      {/* Auto Mode */}
                      <button
                        type="button"
                        onClick={() => setTournamentMode('auto')}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          tournamentMode === 'auto'
                            ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border border-yellow-500/40 shadow-lg shadow-yellow-500/20'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                        }`}
                        title="Auto-Tournament - 3 prompts"
                      >
                        <ZapIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Auto</span>
                      </button>

                      {/* Multiverse Mode */}
                      <button
                        type="button"
                        onClick={() => setTournamentMode('multiverse')}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          tournamentMode === 'multiverse'
                            ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/40 shadow-lg shadow-purple-500/20'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                        }`}
                        title="Parallel Universes - 7 prompts"
                      >
                        <GitBranchIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Multiverse</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mode Description */}
                <div className={`p-3 rounded-xl border transition-all ${
                  tournamentMode === 'multiverse'
                    ? 'bg-purple-500/10 border-purple-500/30'
                    : tournamentMode === 'auto'
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-slate-800/50 border-slate-700/30'
                }`}>
                  {tournamentMode === 'multiverse' ? (
                    <div className="flex items-start gap-3">
                      <GitBranchIcon className="h-5 w-5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-purple-300 font-semibold text-sm">Parallel Universes Mode</p>
                        <p className="text-purple-200/70 text-xs mt-1">
                          Explore ALL possible elimination paths simultaneously. Runs the same prompt through 3 different
                          tournament branches to find the TRUE champion across all universes.
                          <span className="text-purple-400 font-medium"> Costs 7 prompts.</span>
                        </p>
                      </div>
                    </div>
                  ) : tournamentMode === 'auto' ? (
                    <div className="flex items-start gap-3">
                      <ZapIcon className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-300 font-semibold text-sm">Auto-Tournament Mode</p>
                        <p className="text-yellow-200/70 text-xs mt-1">
                          One click runs a full 3-round tournament. AIs battle, critique, and refine responses automatically.
                          Lowest scorer gets eliminated each round until one champion remains.
                          <span className="text-yellow-400 font-medium"> Costs 3 prompts.</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <SwordsIcon className="h-5 w-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-cyan-300 font-semibold text-sm">Manual Mode</p>
                        <p className="text-slate-400 text-xs mt-1">
                          You control the battle. Submit prompts, see all 3 responses, then manually choose which AI to eliminate
                          by clicking "Analyze" on their response. <span className="text-cyan-400 font-medium">1 prompt per action.</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={4}
                    className="w-full p-4 bg-slate-900/70 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none transition-all font-medium leading-relaxed"
                    placeholder={activeThread
                      ? "Continue the conversation..."
                      : "What would you like all three AI models to respond to?"
                    }
                    disabled={loading}
                  />

                  {/* File Upload Component */}
                  <FileUpload
                    files={attachedFiles}
                    onFilesChange={setAttachedFiles}
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="bg-red-900/30 border border-red-600/50 rounded-xl p-4 backdrop-blur-sm">
                    <p className="text-red-300 text-sm font-medium">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={
                    loading ||
                    !prompt.trim() ||
                    (tournamentMode === 'auto' && usage.remaining < 3) ||
                    (tournamentMode === 'multiverse' && usage.remaining < 7)
                  }
                  className={`w-full font-bold py-4 px-6 rounded-xl focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-lg text-lg ${
                    tournamentMode === 'multiverse'
                      ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 hover:from-purple-600 hover:via-pink-600 hover:to-rose-600 focus:ring-purple-500 hover:shadow-purple-500/50 text-white'
                      : tournamentMode === 'auto'
                      ? 'bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-600 hover:via-orange-600 hover:to-red-600 focus:ring-yellow-500 hover:shadow-yellow-500/50 text-white'
                      : 'bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 hover:from-cyan-600 hover:via-blue-700 hover:to-purple-700 focus:ring-cyan-500 hover:shadow-cyan-500/50 text-white'
                  }`}
                  title={
                    tournamentMode === 'multiverse' && usage.remaining < 7
                      ? `Parallel Universes requires 7 prompts. You have ${usage.remaining}.`
                      : tournamentMode === 'auto' && usage.remaining < 3
                      ? `Auto-tournament requires 3 prompts. You have ${usage.remaining}.`
                      : ''
                  }
                >
                  {loading ? (
                    <span className="flex items-center gap-3">
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {tournamentMode === 'multiverse'
                        ? 'Exploring Parallel Universes...'
                        : tournamentMode === 'auto'
                        ? 'Running Tournament...'
                        : 'Forging Battle...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      {tournamentMode === 'multiverse' ? (
                        <>
                          <GitBranchIcon className="h-5 w-5" />
                          {usage.remaining < 7 ? `Need 7 Prompts (Have ${usage.remaining})` : 'Explore Parallel Universes (7 Prompts)'}
                        </>
                      ) : tournamentMode === 'auto' ? (
                        <>
                          <ZapIcon className="h-5 w-5" />
                          {usage.remaining < 3 ? `Need 3 Prompts (Have ${usage.remaining})` : 'Run Auto-Tournament (3 Prompts)'}
                        </>
                      ) : (
                        <>
                          <SendIcon className="h-5 w-5" />
                          {activeThread && activeThread.messages.length > 0 ? 'Continue Battle' : 'Start AI Battle'}
                        </>
                      )}
                    </span>
                  )}
                </button>

                {/* Tournament Progress Indicator - Only shows when loading in auto mode */}
                <TournamentProgress
                  isActive={loading && tournamentMode === 'auto'}
                />

                {/* Multiverse Progress Indicator - Only shows when loading in multiverse mode */}
                <MultiverseProgress
                  isActive={loading && tournamentMode === 'multiverse'}
                />
              </form>
            </div>


            {/* Auto-Tournament Results */}
            {tournamentResults && tournamentResults.success && (
              <TournamentResults
                tournamentData={tournamentResults}
                onClose={() => setTournamentResults(null)}
              />
            )}

            {/* Parallel Universes Results */}
            {parallelUniversesResults && parallelUniversesResults.success && (
              <ParallelUniversesResults
                multiverseData={parallelUniversesResults}
                onClose={() => setParallelUniversesResults(null)}
              />
            )}

            {/* Current Results - Enhanced (Manual Mode) */}
            {currentResults && !tournamentResults && !parallelUniversesResults && (
              <div className="space-y-6">
                {/* Winner Announcement - Stunning Design */}
                <div className="relative overflow-hidden glass border border-yellow-500/30 rounded-2xl p-6 sm:p-8 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 animate-pulse"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-center mb-6">
                      <div className="relative">
                        <SparklesIcon className="h-8 w-8 text-yellow-400 animate-pulse" />
                        <div className="absolute inset-0 h-8 w-8 bg-yellow-400/30 rounded-full blur-xl"></div>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-black text-white ml-3">Latest Battle Results</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                      <div className="glass rounded-xl p-5 border border-yellow-500/20">
                        <p className="text-yellow-300 font-bold text-lg mb-2">🏆 Winner</p>
                        <p className="text-white text-2xl font-black">
                          {currentResults.responses?.[String(currentResults.analysis.winner || '').toLowerCase()]?.model || 'None'}
                        </p>
                      </div>
                      <div className="glass rounded-xl p-5 border border-slate-600/20">
                        <p className="text-slate-300 font-bold text-lg mb-2">Score</p>
                        <p className="text-cyan-400 text-2xl font-black">{Math.round(currentResults.analysis.winnerScore)}/100</p>
                      </div>
                      <div className="glass rounded-xl p-5 border border-slate-600/20">
                        <p className="text-slate-300 font-bold text-lg mb-2">Reason</p>
                        <p className="text-white text-sm font-medium leading-relaxed">{currentResults.analysis.reasoning}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Final Winner Banner */}
                {(currentResults.disqualifiedModels &&
                  currentResults.disqualifiedModels.length >= 2) && (
                  <div className="glass border border-green-500/30 rounded-2xl p-5 shadow-xl">
                    <div className="flex items-center justify-center text-center">
                      <span className="text-green-300 font-bold text-lg">
                        🏆 {resolveFriendlyName(
                              ['gpt','claude','gemini']
                                .find(k => !currentResults.disqualifiedModels.includes(k))
                            )} is the final winner! Start a new prompt to re-engage all models.
                      </span>
                    </div>
                  </div>
                )}

                {/* Model Responses - Full Width Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ModelBox
                  title={settings.gpt?.model || 'GPT-4o Mini'}
                  subtitle={
                    settings.gpt?.personalityId &&
                    mergedPersonalities.gpt?.[settings.gpt.personalityId]
                      ? `${mergedPersonalities.gpt[settings.gpt.personalityId].name} - ${mergedPersonalities.gpt[settings.gpt.personalityId].description}`
                      : 'Default'
                  }
                  result={currentResults.responses.gpt}
                  isWinner={currentResults.analysis.winner === 'GPT'}
                  score={currentResults.analysis.scores.gpt}
                  color="emerald"
                  onAnalyze={currentResults.responses.gpt?.success && (currentResults.disqualifiedModels || []).length < 2
                    ? () => handleAnalyzeResponse(currentResults.prompt, settings.gpt?.model || 'GPT-4o Mini', null)
                    : null}
                  isDisqualified={(currentResults.disqualifiedModels || []).includes("gpt")}
                />
                <ModelBox
                  title={settings.claude?.model || 'Claude Opus 4'}
                  subtitle={
                    settings.claude?.personalityId &&
                    mergedPersonalities.claude?.[settings.claude.personalityId]
                      ? `${mergedPersonalities.claude[settings.claude.personalityId].name} - ${mergedPersonalities.claude[settings.claude.personalityId].description}`
                      : 'Default'
                  }
                  result={currentResults.responses.claude}
                  isWinner={currentResults.analysis.winner === 'CLAUDE'}
                  score={currentResults.analysis.scores.claude}
                  color="blue"
                  onAnalyze={currentResults.responses.claude?.success && (currentResults.disqualifiedModels || []).length < 2
                    ? () => handleAnalyzeResponse(currentResults.prompt, settings.claude?.model || 'Claude Opus 4', null)
                    : null}
                  isDisqualified={(currentResults.disqualifiedModels || []).includes("claude")}
                />
                <ModelBox
                  title={settings.gemini?.model || 'Gemini 2.5 Flash'}
                  subtitle={
                    settings.gemini?.personalityId &&
                    mergedPersonalities.gemini?.[settings.gemini.personalityId]
                      ? `${mergedPersonalities.gemini[settings.gemini.personalityId].name} - ${mergedPersonalities.gemini[settings.gemini.personalityId].description}`
                      : 'Default'
                  }
                  result={currentResults.responses.gemini}
                  isWinner={currentResults.analysis.winner === 'GEMINI'}
                  score={currentResults.analysis.scores.gemini}
                  color="purple"
                  onAnalyze={currentResults.responses.gemini?.success && (currentResults.disqualifiedModels || []).length < 2
                    ? () => handleAnalyzeResponse(currentResults.prompt, settings.gemini?.model || 'Gemini 2.5 Flash', null)
                    : null}
                  isDisqualified={(currentResults.disqualifiedModels || []).includes("gemini")}
                />
              </div>
            </div>
          )}

          {/* Thread Messages */}
          <ThreadMessages
            thread={activeThread}
            loading={!activeThread && activeThreadId && !threadsLoading}
            onAnalyze={handleAnalyzeResponse}
          />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Modals */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} threads={threads} />}
      {showCaveats && <CaveatsModal onClose={() => setShowCaveats(false)} />}
      <SummarizeModal
        isOpen={showSummarizeModal}
        onClose={() => setShowSummarizeModal(false)}
        onConfirm={handleSummarizeConversation}
        loading={summarizing}
        promptsRemaining={usage.remaining}
        currentAgent={settings.soloAgent || 'gpt'}
      />
    </div>
  );
};

export default Forge;
